import { useSyncExternalStore } from "react";
import { AccountType } from "../shared/types/accounts";
import { User } from "../features/user";

export enum FeatureName {
  // SKE-5100: localStorageFeatureToggle.set("SidenavUplift", true), then reload.
  // Use false and reload to restore the previous sidebar.
  SidenavUplift = "SidenavUplift",
  NewAddAccountModal = "NewAddAccountModal",
  MenuColorOption1 = "MenuColorOption1",
  MenuColorOption2 = "MenuColorOption2",
  SocialListening = "SocialListening",
  SLGMB = "SLGMB",
  IdeaPlannerRealtime = "IdeaPlannerRealtime",
  IdeaPlannerVisibleForReview = "IdeaPlannerVisibleForReview",
  InboxTags = "InboxTags",
  // Show Insights → Inbox Metrics: localStorageFeatureToggle.set("InboxInsights", true), then reload.
  InboxInsights = "InboxInsights",
  // Preview the upsell: localStorageFeatureToggle.set("InboxInsightsUpsell", true), then reload.
  InboxInsightsUpsell = "InboxInsightsUpsell",
  KnockPreferences = "KnockPreferences",
  ShowMsTeams = "ShowMsTeams",
  InsightsV2 = "InsightsV2",
  // Flip Social Listening sidebar links to the in-repo /dashboard/insights-v2/
  // paths instead of the legacy /dashboard/social-listening/ paths. The legacy
  // URLs redirect to the same destination via the router, so this toggle only
  // controls which path the sidebar surfaces to the browser. Enable during
  // testing via `localStorage.setItem("InsightsV2Listening", "1")`.
  InsightsV2Listening = "InsightsV2Listening",
  IdeaPlannerTableView = "IdeaPlannerTableView",
  IdeaLink = "IdeaLink",
  DetectPillars = "DetectPillars",
  // SKE-3941: gates the Nango integrations POC card in Settings → Integrations.
  // Enable during testing via `localStorage.setItem("NangoPoc", "1")`.
  NangoPoc = "NangoPoc",
  // SKE-3972: dev override for the External Integrations settings surface. In
  // production the surface is gated by the `external-integrations` entitlement;
  // this localStorage flag lets internal users preview it without the entitlement.
  // Enable via `localStorage.setItem("ExternalIntegrations", "1")`.
  ExternalIntegrations = "ExternalIntegrations",
}

class LocalStorageFeatureToggle {
  set(featureName: `${FeatureName}`, value: boolean) {
    this.validateFeatureName(featureName);

    if (typeof window === "undefined") {
      return;
    }

    try {
      if (value) {
        window.localStorage.setItem(featureName, "1");
      } else {
        window.localStorage.removeItem(featureName);
      }
    } catch {
      // Some browsers throw when storage is disabled. Feature flags should fail closed.
    }
  }

  get(featureName: `${FeatureName}`) {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      return !!window.localStorage.getItem(featureName);
    } catch {
      return false;
    }
  }

  private validateFeatureName(featureName: string) {
    if (!Object.values(FeatureName).includes(featureName as FeatureName)) {
      throw new Error(`Invalid feature name: ${featureName}`);
    }
  }
}

class Deferred<T> {
  public promise: Promise<T>;
  private resolveFunction!: (value: T | PromiseLike<T>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private rejectFunction!: (reason?: any) => void;

  constructor() {
    this.promise = new Promise<T>((resolve, reject) => {
      this.resolveFunction = resolve;
      this.rejectFunction = reject;
    });
  }

  resolve(value: T | PromiseLike<T>) {
    this.resolveFunction(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reject(reason?: any) {
    this.rejectFunction(reason);
  }
}

type AdminPanelToggleName =
  | "enableApprovalAnnotations"
  | "enableFacebookLoginForBusiness"
  | "enableInstagramApiProductTags"
  | "enableInstagramPublishScopeRequest"
  | "enableInstagramReconnectCampaign"
  | "forceInsightsPreview"
  | "forceInstagramPreviewUplift"
  /*
   * SKE-4733. Derived by the server, never set by an admin: the resolved
   * Instagram API publishing gate and the reconnect campaign gate. The client
   * never reads the environment, and both read false until toggles arrive.
   */
  | "instagramApiPublishing"
  | "instagramReconnectCampaign"
  /*
   * SKE-4807. Derived by the server: the campaign is on OR the publish scope
   * request toggle is on. Gates every lost-publishing warning.
   */
  | "instagramPublishAuthenticationRequired"
  | "none";

/**
 * The accounts list is not the only payload that carries admin toggles: a
 * customer connecting their first account has no accounts row yet, so the
 * current-user payload carries the same map. Sources are merged per toggle in
 * arrival order, so an empty or failed fetch of one never blanks the other.
 */
type ToggleSource = "accounts" | "user";

class AdminPanelFeatureToggle {
  private toggleContextDeferred = new Deferred<Record<string, boolean>>();
  private toggleContext: Record<string, boolean> | undefined;
  private sources = new Map<ToggleSource, Record<string, boolean>>();
  private listeners = new Set<() => void>();

  async get(name: AdminPanelToggleName): Promise<boolean> {
    await this.toggleContextDeferred.promise;
    return this.syncGet(name);
  }

  syncGet(name: AdminPanelToggleName): boolean {
    return !!this.toggleContext?.[name];
  }

  isToggleContextReady(): boolean {
    return this.toggleContext !== undefined;
  }

  public resetToggleContext() {
    this.toggleContext = undefined;
    this.sources.clear();
    this.toggleContextDeferred = new Deferred<Record<string, boolean>>();
    this.listeners.forEach((listener) => listener());
  }

  public notifyToggleContext(toggles: Record<string, boolean> | undefined) {
    this.sources.clear();
    this.notifySource("accounts", toggles);
  }

  public subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  public notifyFromAccounts(users: AccountType[] | undefined) {
    /** make sure this function does not block any processing */
    setTimeout(() => {
      const found = users?.find(
        (user) => (user.platformType as string) !== "BRANDGROUPSUMMARY"
      );
      this.notifySource("accounts", found?.featureToggles);
    }, 0);
  }

  /** No accounts payload to read toggles from. Other sources still stand. */
  public notifyAccountsUnavailable() {
    this.notifySource("accounts", undefined);
  }

  public notifyFromUser(
    user: { featureToggles?: Record<string, boolean> } | undefined
  ) {
    this.notifySource("user", user?.featureToggles);
  }

  private notifySource(
    source: ToggleSource,
    toggles: Record<string, boolean> | undefined
  ) {
    // Re-inserting keeps the map in arrival order, so the freshest payload
    // wins on the toggles it actually carries and on nothing else.
    this.sources.delete(source);
    this.sources.set(source, toggles || {});

    const merged: Record<string, boolean> = {};
    this.sources.forEach((sourceToggles) => {
      Object.assign(merged, sourceToggles);
    });

    this.toggleContext = merged;
    this.toggleContextDeferred.resolve(merged);
    this.listeners.forEach((listener) => listener());
  }
}

export const adminPanelFeatureToggle = new AdminPanelFeatureToggle();

export const useAdminPanelToggle = (
  name: AdminPanelToggleName,
  localToggleName?: `${FeatureName}`
) => {
  const value = useSyncExternalStore(
    adminPanelFeatureToggle.subscribe,
    () => adminPanelFeatureToggle.syncGet(name),
    () => false
  );

  if (localToggleName && localStorageFeatureToggle.get(localToggleName)) {
    return true;
  }

  return value;
};

/**
 * SKE-4733. THE client read of the Instagram API publishing gate. Components
 * pass it to `getIGApiPublishingStatusOn` and put it in their memo deps: the
 * accounts source publishes toggles a tick after the reducer, so a memo that
 * missed it would stay false for an enabled workspace. False until toggles
 * arrive, which is the phone track.
 */
export const useInstagramApiPublishingOn = () =>
  useAdminPanelToggle("instagramApiPublishing");

/**
 * SKE-4975. THE client read of the rebuilt Instagram preview.
 *
 * Carries the same timing caveat as the publishing gate above: the accounts
 * source publishes toggles a tick after the reducer, so a memo that reads this
 * must list it as a dependency or an enabled workspace stays on the old
 * preview. False until toggles arrive, which is the preview as it was.
 */
export const useInstagramPreviewUpliftOn = () =>
  useAdminPanelToggle("forceInstagramPreviewUplift");

export const useAdminPanelToggleContextReady = () =>
  useSyncExternalStore(
    adminPanelFeatureToggle.subscribe,
    () => adminPanelFeatureToggle.isToggleContextReady(),
    () => false
  );

class EntitlementFeatureToggle {
  public entitlements: Record<string, string> | undefined;
  public overrideEntitlements: Record<string, string> | undefined;
  notifyUser(user: User) {
    if (user?.preferences?.entitlements) {
      this.entitlements = { ...user.preferences.entitlements };
    }
  }

  get(entitlement: "bulk-create" | "brand-kit") {
    if (this.overrideEntitlements?.[entitlement] !== undefined) {
      return this.overrideEntitlements?.[entitlement] === "true";
    }
    return this.entitlements?.[entitlement] === "true";
  }

  override(entitlements: Record<string, string> | undefined) {
    this.overrideEntitlements = entitlements;
  }
}

export const entitlementFeatureToggle = new EntitlementFeatureToggle();
if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).entitlementFeatureToggle = entitlementFeatureToggle;
}

export const localStorageFeatureToggle = new LocalStorageFeatureToggle();
if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).localStorageFeatureToggle = localStorageFeatureToggle;
}
